'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import Image from 'next/image'

const STORAGE_KEY = 'sevra-install-banner-dismissed'
const IOS_STORAGE_KEY = 'sevra-ios-banner-dismissed'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function isIosSafari(): boolean {
  if (typeof window === 'undefined') return false
  const ua = window.navigator.userAgent
  const isIosDevice = /iPhone|iPad|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const isAppleSafari =
    isIosDevice && ua.includes('Safari') && !ua.includes('CriOS') && !ua.includes('FxiOS')
  const isStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  return isAppleSafari && !isStandalone
}

export default function InstallBanner() {
  const pathname = usePathname()
  const [visible, setVisible] = useState(false)
  const [iosVisible, setIosVisible] = useState(false)
  const promptRef = useRef<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY) && !window.matchMedia('(display-mode: standalone)').matches) {
      function handler(e: Event) {
        e.preventDefault()
        promptRef.current = e as BeforeInstallPromptEvent
        setVisible(true)
      }
      window.addEventListener('beforeinstallprompt', handler)
      return () => window.removeEventListener('beforeinstallprompt', handler)
    }
  }, [])

  useEffect(() => {
    if (!localStorage.getItem(IOS_STORAGE_KEY) && isIosSafari()) {
      setIosVisible(true)
    }
  }, [])

  async function install() {
    const promptEvent = promptRef.current
    if (!promptEvent) return
    await promptEvent.prompt()
    const { outcome } = await promptEvent.userChoice
    if (outcome === 'accepted' || outcome === 'dismissed') {
      setVisible(false)
    }
  }

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, '1')
    setVisible(false)
  }

  function dismissIos() {
    localStorage.setItem(IOS_STORAGE_KEY, '1')
    setIosVisible(false)
  }

  if (pathname === '/' || pathname.startsWith('/onboarding') || pathname.startsWith('/book')) return null

  if (iosVisible) {
    return (
      <div
        style={{ backgroundColor: '#111214', borderTop: '1px solid #262626' }}
        className="fixed bottom-0 left-0 right-0 z-50 flex items-center gap-3 px-4 py-3"
      >
        <Image
          src="/icons/icon-192-v2.png"
          alt="Sevra"
          width={32}
          height={32}
          className="rounded-md shrink-0"
        />
        <p className="flex-1 text-sm text-white leading-snug">
          Install the Sevra app: tap Share then &ldquo;Add to Home Screen&rdquo;
        </p>
        <button
          onClick={dismissIos}
          aria-label="Dismiss"
          className="text-neutral-400 hover:text-white text-lg leading-none px-1 shrink-0"
        >
          ×
        </button>
      </div>
    )
  }

  if (!visible) return null

  return (
    <div
      style={{ backgroundColor: '#111214', borderTop: '1px solid #262626' }}
      className="fixed bottom-0 left-0 right-0 z-50 flex items-center gap-3 px-4 py-3"
    >
      <Image
        src="/icons/icon-192-v2.png"
        alt="Sevra"
        width={32}
        height={32}
        className="rounded-md shrink-0"
      />
      <button
        onClick={install}
        className="flex-1 text-sm text-white leading-snug text-left"
      >
        Install the Sevra app for the best experience
      </button>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-neutral-400 hover:text-white text-lg leading-none px-1 shrink-0"
      >
        ×
      </button>
    </div>
  )
}
